import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Toolbar,
  Tooltip,
  Divider,
  Stack,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import Logo from '@/components/common/Logo';
import { MENU } from '@/routes/menu';
import { ROUTES } from '@/routes/paths';
import { usePing } from '@/hooks/queries/useSystem';

export const DRAWER_WIDTH = 268;
export const DRAWER_WIDTH_COLLAPSED = 80;

function ConnectivityFooter({ mini }) {
  const { data, isError, isLoading } = usePing();
  const online = Boolean(data) && !isError;
  const color = isLoading ? 'warning.main' : online ? 'success.main' : 'error.main';
  const label = isLoading ? 'Checking…' : online ? 'API connected' : 'API offline';

  return (
    <Tooltip title={mini ? label : ''} placement="right">
      <Box
        component={NavLink}
        to={ROUTES.SYSTEM}
        sx={{
          m: 1.5,
          p: mini ? 1 : 1.25,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: mini ? 'center' : 'flex-start',
          gap: 1.25,
          textDecoration: 'none',
          color: 'text.secondary',
          border: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: 'background.neutral',
          transition: 'background-color .2s',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: color,
            flexShrink: 0,
            boxShadow: (t) => `0 0 0 3px ${t.palette.background.neutral}`,
            animation: online ? 'cg-ping 1.8s ease-in-out infinite' : 'none',
            '@keyframes cg-ping': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(5,150,105,0.4)' },
              '50%': { boxShadow: '0 0 0 5px rgba(5,150,105,0)' },
            },
          }}
        />
        {!mini && (
          <Typography variant="caption" fontWeight={600} noWrap sx={{ color }}>
            {label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

/** Navigation drawer. Permanent (collapsible rail) on desktop, temporary overlay on mobile. */
export default function Sidebar({ open, onClose, variant, collapsed = false }) {
  const mini = collapsed && variant === 'permanent';
  const width = mini ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const content = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ px: mini ? 1 : 2.5, justifyContent: mini ? 'center' : 'flex-start' }}>
        <Logo showWordmark={!mini} />
      </Toolbar>
      <Divider />

      <Box sx={{ overflowX: 'hidden', overflowY: 'auto', flex: 1, px: 1.5, py: 2 }}>
        {MENU.map((group) => (
          <Box key={group.section} sx={{ mb: 2 }}>
            {!mini && (
              <Typography
                variant="overline"
                sx={{ px: 1.5, color: 'text.secondary', fontSize: '0.68rem', opacity: 0.8 }}
              >
                {group.section}
              </Typography>
            )}
            <List dense disablePadding sx={{ mt: mini ? 0 : 0.5 }}>
              {group.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Tooltip key={item.path} title={mini ? item.label : ''} placement="right">
                    <ListItemButton
                      component={NavLink}
                      to={item.path}
                      end={item.end}
                      onClick={variant === 'temporary' ? onClose : undefined}
                      sx={{
                        mb: 0.5,
                        py: 1,
                        justifyContent: mini ? 'center' : 'flex-start',
                        px: mini ? 1.5 : 1.75,
                        position: 'relative',
                        color: 'text.secondary',
                        '&.active': {
                          color: 'primary.main',
                          bgcolor: (t) => `${t.palette.primary.main}14`,
                          fontWeight: 700,
                          '& .MuiListItemIcon-root': { color: 'primary.main' },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '22%',
                            height: '56%',
                            width: 3,
                            borderRadius: 4,
                            bgcolor: 'primary.main',
                          },
                        },
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: mini ? 0 : 38 }}>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                      {!mini && (
                        <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2', fontWeight: 'inherit' }} />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Divider />
      <Stack>
        <ConnectivityFooter mini={mini} />
      </Stack>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          borderRight: (t) => `1px solid ${t.palette.divider}`,
          overflowX: 'hidden',
          bgcolor: 'background.paper',
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
        },
      }}
    >
      {content}
    </Drawer>
  );
}
